import { useState } from 'react'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import InPlaceNameValueAccordion from '../../ui-components/InPlaceNameValueAccordion'
import NameValueTable from '../../ui-components/NameValueTable'
import type { TaxLiabilityData } from './utils'
import { TaxCategories } from '../../../types/mutual-fund-detail.types'
import RenderHTML from '../../ui-components/RenderHTML'

type STCGProps = {
  taxLiabilityData: TaxLiabilityData
}

export const STCG = ({ taxLiabilityData }: STCGProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const taxCategory = taxLiabilityData?.taxCategory

  const getSTCGTitle = () => {
    if (
      [
        TaxCategories.HYBRID_NON_EQUITY,
        TaxCategories.NON_EQUITY,
        TaxCategories.DEBT,
        TaxCategories.HYBRID_DEBT
      ].includes(taxCategory as TaxCategories)
    ) {
      return `STCG Tax as per income tax slab `
    }
    return `STCG Tax @ ${taxLiabilityData?.stcgTaxInfo?.taxRate}%`
  }

  const getSTCGValueText = () => {
    if ([TaxCategories.HYBRID_NON_EQUITY, TaxCategories.NON_EQUITY].includes(taxCategory as TaxCategories)) {
      return `Investment held for less than 2Y`
    }
    if (
      [TaxCategories.EQUITY, TaxCategories.HYBRID_EQUITY, TaxCategories.ELSS].includes(taxCategory as TaxCategories)
    ) {
      return `Investment held for less than 1Y`
    }
    if ([TaxCategories.DEBT, TaxCategories.HYBRID_DEBT].includes(taxCategory as TaxCategories)) {
      return `Investments made on or after 1 Apr ‘23`
    }
    return 'Investment held for less than 1Y'
  }

  const getBottomDescription = () => {
    if (
      [
        TaxCategories.HYBRID_NON_EQUITY,
        TaxCategories.NON_EQUITY,
        TaxCategories.DEBT,
        TaxCategories.HYBRID_DEBT
      ].includes(taxCategory as TaxCategories)
    ) {
      return `Above tax calculation <span class="text-black font-[500]">assumes a 30% income tax slab</span>. 
      <br/>Actual tax may vary with your income bracket.`
    }
    return ''
  }

  return (
    <div
      style={{
        width: isExpanded ? 'calc(100% + 24px)' : '100%',
        marginLeft: isExpanded ? '-12px' : '0px'
      }}
      className={`w-full ${isExpanded ? 'pb-3' : 'pb-4'}`}
    >
      <InPlaceNameValueAccordion
        name={`${getSTCGTitle()}`}
        value={`${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.stcgTaxInfo?.taxableAmount)}`}
        nameClassName='!text-[#095EB2] !font-[500] !text-[13px]'
        valueClassName='!text-[#095EB2] !text-[13px]'
        chevronColor='#095EB2'
        accordionClassName={isExpanded ? '!bg-[#E6F2FF]' : ''}
        isExpanded={isExpanded}
        alwaysShowValue={true}
        onAccordionClick={() => setIsExpanded((isExpandedPrev) => !isExpandedPrev)}
      >
        <NameValueTable
          data={[
            {
              name: `${getSTCGValueText()}`,
              value: `${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.stcgTaxInfo?.investedAmount)}`
            },
            {
              name: 'Short term capital gains',
              value: `${taxLiabilityData?.stcgTaxInfo?.gains < 0 ? '-' : ''}${roundOffAndFormatNumberWithCurrency(Math.abs(taxLiabilityData?.stcgTaxInfo?.gains ?? 0))}`
            }
          ]}
          nameClassName='!text-[#3D3D3D] !text-[13px]'
          valueClassName='!text-[13px]'
          gap={4}
        />
        {getBottomDescription() && (
          <>
            <hr className='mt-2 border-[#3D3D3D] opacity-10' />
            <div className='text-[#6A6E6E] font-[400] text-[13px] pt-2'>
              <RenderHTML innerHTMLText={getBottomDescription()} />
            </div>
          </>
        )}
      </InPlaceNameValueAccordion>
    </div>
  )
}
